import { useEffect, useRef, useState } from 'react';
import { Search, X, UserSearch, Loader2 } from 'lucide-react';
import { PatientService } from '@/services/firebase/patients';
import { PatientMetaLines } from '@/components/patients/PatientMetaLines';
import type { Patient } from '@/types/models';

interface CitePatientButtonProps {
  clinicId: string | null | undefined;
  /** Disparado quando o usuário escolhe um paciente para citar na mensagem. */
  onPick: (patient: Patient) => void;
  disabled?: boolean;
  /** Layout enxuto (ícone menor) para o chat flutuante. */
  compact?: boolean;
}

/**
 * Botão "Citar paciente" para a barra de composição do chat. Abre um popover
 * com busca de paciente (nome ou CPF) mostrando nome + CPF + nascimento, e
 * devolve o paciente escolhido via onPick — o chamador anexa o vínculo à
 * próxima mensagem (linkedPatientId/patientLinked).
 */
export function CitePatientButton({ clinicId, onPick, disabled, compact }: CitePatientButtonProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Patient[] | null>(null);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fecha ao clicar fora.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Foca o input ao abrir.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Busca debounced (250 ms) a partir de 2+ caracteres.
  useEffect(() => {
    if (!open || !clinicId) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) { setResults(null); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const digitsOnly = trimmed.replace(/\D/g, '');
        const looksLikeCpf = digitsOnly.length >= 2 && /^[\d.\-/]+$/.test(trimmed);
        const res = await PatientService.searchPatients(clinicId, trimmed, looksLikeCpf ? 'cpf' : 'name');
        setResults(res);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, open, clinicId]);

  const pick = (p: Patient) => {
    onPick(p);
    setOpen(false);
    setQuery('');
    setResults(null);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className={`${compact ? 'p-1' : 'p-2'} text-on-surface-variant hover:text-primary disabled:opacity-30`}
        title="Citar paciente na mensagem"
        aria-label="Citar paciente na mensagem"
      >
        <UserSearch size={compact ? 14 : 20} />
      </button>

      {open && (
        <div className="absolute bottom-full mb-2 left-0 z-50 w-72 bg-surface border border-outline-variant/20 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-outline/10 flex items-center gap-2">
            <Search size={14} className="text-on-surface-variant shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
              placeholder="Nome ou CPF (mín. 2)…"
              className="flex-1 bg-transparent outline-none text-sm text-on-surface placeholder-on-surface-variant"
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-on-surface-variant hover:text-on-surface shrink-0"
              aria-label="Fechar"
            >
              <X size={14} />
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {loading && (
              <div className="px-3 py-3 text-xs text-on-surface-variant flex items-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Buscando…
              </div>
            )}
            {!loading && query.trim().length < 2 && (
              <div className="px-3 py-3 text-xs text-on-surface-variant">Digite ao menos 2 caracteres.</div>
            )}
            {!loading && query.trim().length >= 2 && results && results.length === 0 && (
              <div className="px-3 py-3 text-xs text-on-surface-variant">Nenhum paciente encontrado.</div>
            )}
            {!loading && results && results.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => pick(p)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-primary/10 transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-primary-container text-primary flex items-center justify-center text-xs font-bold shrink-0">
                  {p.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-on-surface truncate">{p.name}</div>
                  <PatientMetaLines p={p} className="text-[11px] text-on-surface-variant truncate" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
